import { Link, router } from "@inertiajs/react";
import { useState, useMemo, useRef, useEffect } from "react";
import { Archive, BriefcaseBusiness, CheckCheck, ChevronDown, ChevronLeft, ChevronRight, MapPin, Sparkles, X } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { LOCATION_TREE, LocationNode } from "../../data/leopard_locations";
import { PRACTICE_AREAS } from "../../data/leopard_options";

interface Job {
  id: number;
  firm_name: string;
  title: string;
  locations: string[];
  practice_area: string;
  jd_year_range: string;
  job_type: string;
  status: string;
  sourcing_status: string;
  strong_count: number;
  possible_count: number;
  job_alert_id: number | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  dnc_firm_status: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

interface Props {
  jobs: Job[];
  new_count: number;
  archived_count: number;
  ai_assist: boolean;
  pagination: Pagination;
}

type DisplayStatus = "unmatched" | "matched" | "archived";

function displayStatus(j: Job): DisplayStatus {
  if (j.sourcing_status === "skipped") return "archived";
  // Strong+Possible only (task b5341df6) — a job with only Weak matches is
  // still "unmatched" for triage purposes, same as the Show page default.
  if (j.strong_count > 0 || j.possible_count > 0) return "matched";
  return "unmatched";
}

const STATUS_CONFIG: Record<DisplayStatus, { label: string; className: string }> = {
  unmatched: { label: "Unmatched", className: "bg-green-50 text-green-700 border-green-200" },
  matched:   { label: "Matched",   className: "bg-purple-50 text-purple-700 border-purple-200" },
  archived:  { label: "Archived",  className: "bg-gray-100 text-gray-500 border-gray-200" },
};

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Built once at module load: every leaf city → its top-level state/region name
const CITY_TO_STATE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  function traverse(nodes: LocationNode[], stateName: string) {
    for (const node of nodes) {
      if (!node.children) {
        map.set(node.name, stateName);
      } else {
        traverse(node.children, stateName);
      }
    }
  }
  for (const state of LOCATION_TREE) {
    traverse(state.children ?? [], state.name);
  }
  return map;
})();

interface ComboboxOption {
  value: string;
  label: string;
  group?: string;
}

function SearchableCombobox({
  value,
  onChange,
  options,
  allLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.group ?? "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, ComboboxOption[]>();
    for (const opt of filtered) {
      const g = opt.group ?? "";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(opt);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const selectedLabel =
    value === "all" ? null : (options.find((o) => o.value === value)?.label ?? value);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 text-sm border rounded-lg px-2.5 py-1.5 bg-white transition-colors ${
          value !== "all"
            ? "border-green-400 text-green-700"
            : "border-gray-200 text-gray-600 hover:border-gray-300"
        }`}
      >
        <span className="max-w-[140px] truncate">{selectedLabel ?? allLabel}</span>
        {value !== "all" ? (
          <X
            className="w-3.5 h-3.5 flex-shrink-0 opacity-60 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onChange("all"); setOpen(false); }}
          />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
        )}
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-60 flex flex-col"
          style={{ maxHeight: "280px" }}
        >
          <div className="p-2 border-b border-gray-100 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
              placeholder="Search..."
              className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-green-400"
            />
          </div>
          <div className="overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange("all"); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                value === "all" ? "text-green-700 font-medium bg-green-50" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {allLabel}
            </button>
            {grouped.map(([group, opts]) => (
              <div key={group}>
                {group && (
                  <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-t border-gray-100">
                    {group}
                  </div>
                )}
                {opts.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                      value === opt.value
                        ? "text-green-700 font-medium bg-green-50"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobAlertsIndex({ jobs, new_count, archived_count, ai_assist, pagination }: Props) {
  const [statusFilter, setStatusFilter]     = useState<string>("unmatched");
  const [practiceFilter, setPracticeFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [showFlagged, setShowFlagged]       = useState(false);
  const [selected, setSelected]             = useState<Set<number>>(new Set());
  const [acting, setActing]                 = useState(false);

  const practiceOptions = useMemo((): ComboboxOption[] => {
    const inJobs = new Set(jobs.map((j) => j.practice_area).filter(Boolean));
    return PRACTICE_AREAS.filter((pa) => inJobs.has(pa)).map((pa) => ({ value: pa, label: pa }));
  }, [jobs]);

  const locationOptions = useMemo((): ComboboxOption[] => {
    const cities = new Set(jobs.flatMap((j) => j.locations).filter(Boolean));
    return Array.from(cities)
      .sort()
      .map((city) => ({ value: city, label: city, group: CITY_TO_STATE.get(city) }));
  }, [jobs]);

  const filtered = useMemo(() => jobs.filter((j) => {
    if (statusFilter !== "all" && displayStatus(j) !== statusFilter) return false;
    if (practiceFilter !== "all" && j.practice_area !== practiceFilter) return false;
    if (locationFilter !== "all" && !j.locations.includes(locationFilter)) return false;
    if (showFlagged && j.dnc_firm_status === "clear") return false;
    return true;
  }), [jobs, statusFilter, practiceFilter, locationFilter, showFlagged]);

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((j) => j.id))
    );
  }

  function bulkAction(url: string) {
    if (selected.size === 0 || acting) return;
    setActing(true);
    router.post(url, { ids: Array.from(selected) }, {
      onFinish: () => { setActing(false); setSelected(new Set()); },
    });
  }

  return (
    <AppLayout title="Job Alerts">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Alerts</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Leopard jobs · <span className="font-medium text-gray-700">{new_count}</span> unmatched
            {archived_count > 0 && <> · {archived_count} archived</>}
          </p>
        </div>
        {ai_assist && (
          <span className="text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> AI Assist on
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {(["all", "unmatched", "matched", "archived"] as const).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 font-medium transition-colors ${
                statusFilter === s ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
              }`}>
              {s === "all" ? "All" : STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>

        {practiceOptions.length > 0 && (
          <SearchableCombobox
            value={practiceFilter}
            onChange={setPracticeFilter}
            options={practiceOptions}
            allLabel="All Practice Areas"
          />
        )}

        {locationOptions.length > 0 && (
          <SearchableCombobox
            value={locationFilter}
            onChange={setLocationFilter}
            options={locationOptions}
            allLabel="All Locations"
          />
        )}

        <button
          onClick={() => setShowFlagged((p) => !p)}
          className={`text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${
            showFlagged
              ? "bg-red-600 text-white border-red-600"
              : "text-red-600 border-red-200 bg-white hover:bg-red-50"
          }`}
        >
          Flagged only
        </button>

        <span className="text-sm text-gray-400 ml-auto">{filtered.length} jobs</span>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-3">
          <span className="text-sm text-blue-700 font-medium">{selected.size} selected</span>
          <button onClick={() => bulkAction("/job-alerts/mark-handled")} disabled={acting}
            className="ml-2 flex items-center gap-1 text-xs font-medium text-green-700 border border-green-200 bg-white hover:bg-green-50 rounded px-2.5 py-1 disabled:opacity-50">
            <CheckCheck className="w-3.5 h-3.5" /> Mark Handled
          </button>
          <button onClick={() => bulkAction("/job-alerts/archive")} disabled={acting}
            className="flex items-center gap-1 text-xs font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 rounded px-2.5 py-1 disabled:opacity-50">
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-blue-500 hover:underline">
            Clear
          </button>
        </div>
      )}

      {/* Job list */}
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
        {filtered.length > 0 && (
          <div className="px-4 py-2 flex items-center gap-3 bg-gray-50 rounded-t-lg">
            <input type="checkbox" className="rounded border-gray-300"
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleAll} />
            <span className="text-xs text-gray-500">Select all</span>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <BriefcaseBusiness className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No jobs match the current filters.</p>
          </div>
        )}

        {filtered.map((job) => {
          const ds = displayStatus(job);
          const cfg = STATUS_CONFIG[ds];

          return (
            <div key={job.id} className={`px-4 py-3 flex items-start gap-3 ${selected.has(job.id) ? "bg-blue-50" : "hover:bg-gray-50"}`}>
              <input type="checkbox" className="rounded border-gray-300 mt-0.5"
                checked={selected.has(job.id)} onChange={() => toggleSelect(job.id)} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link href={`/job-alerts/${job.id}`} className="text-sm font-semibold text-gray-900 hover:text-green-700">
                    {job.firm_name}
                  </Link>
                  {job.title && <span className="text-sm text-gray-500 truncate">{job.title}</span>}
                  <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${cfg.className}`}>
                    {cfg.label}
                  </span>
                  {job.dnc_firm_status === "nationwide_block" && (
                    <span className="text-xs px-1.5 py-0.5 rounded border font-medium bg-red-50 text-red-700 border-red-200">DNC</span>
                  )}
                  {job.dnc_firm_status === "office_block" && (
                    <span className="text-xs px-1.5 py-0.5 rounded border font-medium bg-amber-50 text-amber-700 border-amber-200">DNC (office)</span>
                  )}
                  {job.dnc_firm_status === "needs_review" && (
                    <span className="text-xs px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-500 border-gray-200">DNC review</span>
                  )}
                  {job.strong_count > 0 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                      {job.strong_count} strong
                    </span>
                  )}
                  {job.possible_count > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                      {job.possible_count} possible
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  {job.practice_area && (
                    <span className="text-xs text-gray-500">{job.practice_area}</span>
                  )}
                  {job.jd_year_range && (
                    <span className="text-xs text-gray-400">JD {job.jd_year_range}</span>
                  )}
                  {job.locations.length > 0 && (
                    <span className="text-xs text-gray-400 flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />{job.locations.join(", ")}
                    </span>
                  )}
                </div>

                {ai_assist && ds !== "archived" && (
                  <button onClick={() => router.post(`/job-alerts/${job.id}/match`)}
                    className="mt-1.5 text-xs text-purple-700 border border-purple-200 hover:bg-purple-50 rounded px-2 py-0.5 flex items-center gap-1 transition-colors">
                    <Sparkles className="w-3 h-3" />
                    {ds === "matched" ? "Re-match Candidates" : "Match Candidates"}
                  </button>
                )}
              </div>

              <div className="text-xs text-gray-400 shrink-0 text-right">
                <div>{fmt(job.first_seen_at)}</div>
                {job.last_seen_at && job.last_seen_at !== job.first_seen_at && (
                  <div className="text-gray-300">updated {fmt(job.last_seen_at)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4 fab-clearance text-sm text-gray-500">
          <span>
            Page {pagination.page} of {pagination.total_pages} &middot; {pagination.total_count} total
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={pagination.page <= 1}
              onClick={() => router.get("/job-alerts", { page: pagination.page - 1 }, { preserveScroll: false })}
              className="flex items-center gap-0.5 px-2.5 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => router.get("/job-alerts", { page: pagination.page + 1 }, { preserveScroll: false })}
              className="flex items-center gap-0.5 px-2.5 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
